/**
 * UIComponents.tsx — Core UI Primitives & Barrel Re-exports
 *
 * This file contains small, foundational UI components (Button, Input, Card, etc.)
 * and the FahryoLogo branding system. Larger components live in their own files
 * and are re-exported here for backward compatibility.
 *
 * Components defined here:
 *   Button, Input, Card, ProgressBar, Toggle, FahryoLogo,
 *   SyncIndicator, ConsentBanner, Header
 *
 * Re-exported from dedicated files:
 *   LegalModal, OnboardingModal, PaywallModal, BadgeIcon, BadgeModal
 */

import React, { useState, useEffect } from 'react';
import logger from '../services/logger';
import { Cloud, CloudOff, RefreshCw, ShieldCheck } from 'lucide-react';
import { supabase } from '../services/supabaseClient';
import { useTranslation } from 'react-i18next';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL || '';

// ─── Logo Settings & Caching ─────────────────────────────────────
interface LogoSettings {
  logoUrl: string;
  logoUrlDark: string;
  logoHeightClient: number;
}

const logoCache: { settings: LogoSettings | null; fetchedAt: number } = {
  settings: null,
  fetchedAt: 0,
};
const CACHE_TTL = 30 * 1000; // 30 seconds — short so admin changes take effect quickly
const LOGO_CACHE_KEY = 'fahryo_logo_settings';

function extractValue(jsonbVal: unknown): string {
  if (typeof jsonbVal === 'string') {
    // JSONB stores strings as `"value"` — try JSON.parse first, then strip quotes
    try {
      return JSON.parse(jsonbVal);
    } catch {
      /* intentional: JSON.parse fallback to regex strip */
    }
    return jsonbVal.replace(/^"|"$/g, '');
  }
  return String(jsonbVal ?? '');
}

async function fetchLogoSettings(): Promise<LogoSettings> {
  const defaults: LogoSettings = {
    logoUrl: '/fahryo_logo.png',
    logoUrlDark: '',
    logoHeightClient: 40,
  };
  try {
    const { data } = await supabase
      .from('app_settings')
      .select('key, value')
      .in('key', ['logo_url', 'logo_url_dark', 'logo_height_client']);

    if (data && data.length > 0) {
      const result = { ...defaults };
      for (const row of data) {
        const val = extractValue(row.value);
        if (row.key === 'logo_url' && val) {
          if (val.startsWith('http')) {
            result.logoUrl = val;
          } else if (val === 'fahryo_logo.png' || val === '/fahryo_logo.png') {
            result.logoUrl = '/fahryo_logo.png';
          } else {
            result.logoUrl = `${SUPABASE_URL}/storage/v1/object/public/logos/${val}`;
          }
        }
        if (row.key === 'logo_url_dark' && val) {
          if (val.startsWith('http')) {
            result.logoUrlDark = val;
          } else {
            result.logoUrlDark = `${SUPABASE_URL}/storage/v1/object/public/logos/${val}`;
          }
        }
        if (row.key === 'logo_height_client') {
          result.logoHeightClient = parseInt(val) || 40;
        }
      }
      logoCache.settings = result;
      logoCache.fetchedAt = Date.now();
      // Persist to localStorage for instant next-load
      try {
        localStorage.setItem(LOGO_CACHE_KEY, JSON.stringify(result));
      } catch {}
      return result;
    }
  } catch (err) {
    logger.error('UIComponents', 'Logo settings fetch failed:', err);
  }
  return defaults;
}

function getInitialLogoSettings(): LogoSettings {
  // Priority: in-memory cache > localStorage > fallback
  if (logoCache.settings) return logoCache.settings;
  try {
    const stored = localStorage.getItem(LOGO_CACHE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as LogoSettings;
      if (parsed.logoUrl) {
        logoCache.settings = parsed;
        return parsed;
      }
    }
  } catch {}
  return { logoUrl: '/fahryo_logo.png', logoUrlDark: '', logoHeightClient: 40 };
}

function useLogoSettings(): LogoSettings {
  const [settings, setSettings] = useState<LogoSettings>(getInitialLogoSettings);
  useEffect(() => {
    const load = async (force = false) => {
      if (!force && logoCache.settings && Date.now() - logoCache.fetchedAt < CACHE_TTL) {
        setSettings(logoCache.settings);
        return;
      }
      const result = await fetchLogoSettings();
      setSettings(result);
    };

    load();

    // Re-fetch when tab becomes visible (e.g. after admin changes)
    const onVisibility = () => {
      if (document.visibilityState === 'visible') load(true);
    };
    document.addEventListener('visibilitychange', onVisibility);

    // Listen for explicit refresh events
    const onLogoUpdated = () => load(true);
    window.addEventListener('fahryo-logo-updated', onLogoUpdated);

    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('fahryo-logo-updated', onLogoUpdated);
    };
  }, []);

  return settings;
}

// ─── Primitive Components ─────────────────────────────────────────

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
  fullWidth?: boolean;
}

export const Button: React.FC<ButtonProps> = ({
  children,
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  className = '',
  ...props
}) => {
  const baseStyle =
    'rounded-xl font-bold transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center';

  const sizeStyles = {
    sm: 'py-2 px-4 text-sm',
    md: 'py-3 px-6',
    lg: 'py-4 px-8 text-lg',
  };

  const variants = {
    primary:
      'bg-primary text-white shadow-lg shadow-orange-300/40 dark:shadow-none hover:bg-[var(--f-brand-hover)] [box-shadow:0_8px_24px_rgba(224,55,5,0.3),inset_0_1px_0_rgba(255,255,255,0.25)]',
    secondary:
      'bg-secondary text-white hover:bg-[var(--f-secondary-hover)] [box-shadow:0_8px_24px_rgba(173,133,90,0.28),inset_0_1px_0_rgba(255,255,255,0.2)]',
    outline:
      'border-2 border-primary text-primary bg-white/30 dark:bg-white/5 backdrop-blur-md hover:bg-orange-50/60 dark:hover:bg-white/10',
    ghost:
      'text-[var(--f-text-secondary)] bg-white/25 dark:bg-white/5 backdrop-blur-md hover:bg-white/45 dark:hover:bg-white/10',
  };

  return (
    <button
      className={`${baseStyle} ${sizeStyles[size]} ${variants[variant]} ${fullWidth ? 'w-full' : ''} ${className}`}
      {...props}
    >
      {children}
    </button>
  );
};

export const Input: React.FC<
  React.InputHTMLAttributes<HTMLInputElement> & { label?: string; icon?: React.ReactNode }
> = ({ label, icon, className = '', ...props }) => {
  return (
    <div className="w-full mb-4">
      {label && (
        <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5 ml-1">
          {label}
        </label>
      )}
      <div className="relative">
        <input
          className={`glass-input w-full rounded-xl px-4 py-3 text-[var(--f-text)] outline-none transition-colors focus:border-primary focus:bg-white/55 dark:focus:bg-white/10 ${icon ? 'pl-11' : ''} ${className}`}
          {...props}
        />
        {icon && (
          <div className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--f-text-tertiary)] pointer-events-none">
            {icon}
          </div>
        )}
      </div>
    </div>
  );
};

export const Card: React.FC<{
  children: React.ReactNode;
  className?: string;
  onClick?: () => void;
}> = ({ children, className = '', onClick }) => (
  <div
    onClick={onClick}
    className={`glass-card p-4 ${onClick ? 'glass-interactive cursor-pointer' : ''} ${className}`}
  >
    {children}
  </div>
);

export const ProgressBar: React.FC<{ progress: number; color?: string }> = ({
  progress,
  color = 'bg-primary',
}) => (
  <div className="w-full bg-[var(--f-surface-elevated)] rounded-full h-2.5">
    <div
      className={`${color} h-2.5 rounded-full transition-all duration-500`}
      style={{ width: `${progress}%` }}
    ></div>
  </div>
);

export const Toggle: React.FC<{ checked: boolean; onChange: () => void }> = ({
  checked,
  onChange,
}) => (
  <div
    onClick={onChange}
    className={`w-12 h-6 rounded-full flex items-center p-1 cursor-pointer transition-colors shadow-[inset_0_1px_2px_rgba(0,0,0,0.18)] ${checked ? 'bg-primary' : 'bg-gray-300 dark:bg-gray-600 border border-black/10 dark:border-white/10'}`}
  >
    <div
      className={`w-4 h-4 rounded-full bg-white shadow-md ring-1 ring-black/5 transform transition-transform ${checked ? 'translate-x-6' : 'translate-x-0'}`}
    />
  </div>
);

// ─── Branding ─────────────────────────────────────────────────────

export const FahryoLogo: React.FC<{
  className?: string;
  /** Rendert das Logo auf einer weißen, abgerundeten Glas-Kachel */
  tile?: boolean;
  /** Zusätzliche Klassen für die Kachel (z. B. Padding/Größe) */
  tileClassName?: string;
}> = ({ className, tile = false, tileClassName = '' }) => {
  const { logoUrl, logoUrlDark, logoHeightClient } = useLogoSettings();
  const [isDark, setIsDark] = useState(document.documentElement.classList.contains('dark'));
  const activeLogo = isDark && logoUrlDark ? logoUrlDark : logoUrl;

  // Start visible if logo is likely cached (from localStorage → browser image cache)
  const [imgLoaded, setImgLoaded] = useState(() => {
    if (typeof document === 'undefined') return false;
    // Check if browser already has this image cached
    const img = new Image();
    img.src = activeLogo;
    return img.complete;
  });

  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDark(document.documentElement.classList.contains('dark'));
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  // Reset loaded state only when logo URL actually changes to a new value
  const prevLogoRef = React.useRef(activeLogo);
  useEffect(() => {
    if (prevLogoRef.current !== activeLogo) {
      prevLogoRef.current = activeLogo;
      const img = new Image();
      img.src = activeLogo;
      setImgLoaded(img.complete);
    }
  }, [activeLogo]);

  const img = (
    <img
      src={activeLogo}
      alt="Fahryo"
      style={{
        height: logoHeightClient,
        width: 'auto',
        objectFit: 'contain',
        opacity: imgLoaded ? 1 : 0,
        transition: imgLoaded ? 'none' : 'opacity 0.2s ease-in',
      }}
      onLoad={() => setImgLoaded(true)}
      onError={(e) => {
        (e.target as HTMLImageElement).src = '/fahryo_logo.png';
      }}
    />
  );

  if (tile) {
    return (
      <div
        className={`glass-tile inline-flex items-center justify-center select-none px-4 py-2.5 ${tileClassName}`}
      >
        <div className={`flex items-center justify-center ${className || ''}`}>{img}</div>
      </div>
    );
  }

  return <div className={`flex items-center select-none ${className || ''}`}>{img}</div>;
};

// ─── Layout Components ────────────────────────────────────────────

export const SyncIndicator: React.FC = () => {
  const [status, setStatus] = useState<'synced' | 'syncing' | 'offline'>('synced');

  useEffect(() => {
    const updateStatus = () => setStatus(navigator.onLine ? 'synced' : 'offline');

    const handleSync = () => {
      if (!navigator.onLine) return;
      setStatus('syncing');
      setTimeout(() => setStatus('synced'), 1500);
    };

    window.addEventListener('online', updateStatus);
    window.addEventListener('offline', updateStatus);
    window.addEventListener('fahryo-data-updated', handleSync);

    // Initial check
    updateStatus();

    return () => {
      window.removeEventListener('online', updateStatus);
      window.removeEventListener('offline', updateStatus);
      window.removeEventListener('fahryo-data-updated', handleSync);
    };
  }, []);

  return (
    <div className="glass-pill flex items-center justify-center w-8 h-8 transition-colors">
      {status === 'offline' && (
        <CloudOff size={14} className="text-gray-400" aria-label="Offline" />
      )}
      {status === 'syncing' && (
        <RefreshCw size={14} className="text-primary animate-spin" aria-label="Synchronisiere..." />
      )}
      {status === 'synced' && (
        <Cloud
          size={14}
          className="text-green-500 fill-green-50 dark:fill-green-900/30"
          aria-label="Synchronisiert"
        />
      )}
    </div>
  );
};

export const ConsentBanner: React.FC<{
  onAccept: () => void;
  onDecline: () => void;
  isVisible: boolean;
}> = ({ onAccept, onDecline, isVisible }) => {
  const { t } = useTranslation();
  if (!isVisible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 p-4 z-50 animate-in slide-in-from-bottom-full">
      <div className="glass-strong p-5 max-w-lg mx-auto">
        <div className="flex items-start gap-4 mb-4">
          <div className="glass-pill w-10 h-10 flex items-center justify-center flex-shrink-0 !bg-orange-100/70 dark:!bg-orange-900/30">
            <ShieldCheck className="text-primary" size={20} />
          </div>
          <div>
            <h3 className="font-bold text-[var(--f-text)] text-lg">{t('consent_title')}</h3>
            <p className="text-sm text-[var(--f-text-muted)] mt-1">{t('consent_description')}</p>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row gap-3">
          <Button variant="outline" fullWidth onClick={onDecline} className="text-sm py-2">
            {t('consent_decline')}
          </Button>
          <Button variant="primary" fullWidth onClick={onAccept} className="text-sm py-2">
            {t('consent_accept')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export const Header: React.FC<{
  title?: string;
  subtitle?: string;
  rightContent?: React.ReactNode;
  showLogo?: boolean;
}> = ({ title, subtitle, rightContent, showLogo = false }) => (
  <header
    className="flex justify-between items-start mb-6 gap-3"
    style={{ paddingTop: 'max(0.5rem, env(safe-area-inset-top))' }}
  >
    <div className="min-w-0 flex-1">
      {showLogo ? (
        <FahryoLogo tile tileClassName="mb-1" />
      ) : (
        title && <h1 className="text-2xl font-bold text-[var(--f-text)] truncate">{title}</h1>
      )}
      {subtitle && <p className="text-[var(--f-text-muted)] text-sm mt-1 truncate">{subtitle}</p>}
    </div>
    <div className="flex items-start gap-3 flex-shrink-0">
      <SyncIndicator />
      {rightContent && <div className="flex flex-col items-end">{rightContent}</div>}
    </div>
  </header>
);

// ─── Re-exports (backward compat — consumers import from here) ───
export { LegalModal } from './LegalModal';
export { OnboardingModal } from './OnboardingModal';
export { PaywallModal } from './PaywallModal';
export { BadgeIcon, BadgeModal } from './BadgeComponents';
